import { ImageSourceSizes, Typography, getTypographyDefaultColor } from '@ag1/ui';
import { toPlainText } from '@portabletext/react';
import { internalLinkClickEvent } from 'analytics/events/studio';
import clsx from 'clsx';
import { useMemo } from 'react';
import { useLocale } from 'shared/hooks/useLocale';
import CustomPortableText from 'shared/studio/PortableText/PortableText';
import { StudioBaseImageV2 } from 'shared/studio/StudioBaseImage/StudioBaseImage';
import { getSanityLinkUrl } from 'shared/utils/getSanityLinkUrl';
import { getThemeCSSClass } from 'shared/utils/getThemeCSSClass';
import { ValuePropCardsV2 } from 'types/studio/studio';
import styles from './ValuePropCardsV2Item.module.scss';

type Props = { sectionType: string; activeState?: boolean; imageSizes: ImageSourceSizes } & Pick<
  ValuePropCardsV2,
  'cardsInRow' | 'addSlider' | 'theme' | 'isFullWidth'
> &
  Omit<ValuePropCardsV2['cards'][number], '_key' | '_type'>;

const ValuePropCardsV2Item = ({
  sectionType,
  portableText,
  image,
  description,
  addLink,
  link,
  cardsInRow,
  addSlider,
  activeState,
  isFullWidth,
  theme,
  imageSizes,
  visibleOnPageLoad
}: Props & { visibleOnPageLoad: boolean }) => {
  const locale = useLocale();

  const linkUrl = useMemo(() => getSanityLinkUrl(link, locale), [locale, link]);

  const sendClickEvent = () => {
    internalLinkClickEvent({
      clickText: portableText ? toPlainText(portableText) : '',
      location: sectionType,
      url: linkUrl
    });
  };

  return (
    <div
      className={clsx(
        addSlider ? styles.itemSlide : '',
        addSlider && activeState && styles.active
      )}>
      {addLink && link && image.image && (
        <a href={linkUrl} onClick={sendClickEvent}>
          <StudioBaseImageV2
            image={image.image}
            tabletImage={image.tabletImage}
            mobileImage={image.mobileImage}
            sizes={imageSizes}
            eagerLoading={visibleOnPageLoad}
            className={clsx(
              styles.imgCont,
              isFullWidth && styles.fullWidthImg,
              styles.hoverImage,
              cardsInRow === 'three' && styles.three,
              addSlider && styles.imgContSlider
            )}
            childClassName={addSlider ? styles.radius : ''}
          />
        </a>
      )}
      {!addLink && image.image && (
        <StudioBaseImageV2
          image={image.image}
          tabletImage={image.tabletImage}
          mobileImage={image.mobileImage}
          withMobileImage={image.withMobileImage}
          sizes={imageSizes}
          eagerLoading={visibleOnPageLoad}
          className={clsx(
            styles.imgCont,
            isFullWidth && styles.fullWidthImg,
            cardsInRow === 'three' && styles.three,
            addSlider && styles.imgContSlider
          )}
          childClassName={addSlider ? styles.radius : ''}
        />
      )}
      <div className={clsx(styles.text, addSlider && styles.center)}>
        {addLink && link && (
          <a href={linkUrl} onClick={sendClickEvent}>
            <Typography
              variant="subtitle"
              size="1"
              component="div"
              noPadding
              weight="700"
              className={clsx(
                styles.title,
                addSlider && styles.titleSlider,
                getThemeCSSClass(theme)
              )}
              color={getTypographyDefaultColor(theme)}>
              {portableText && <CustomPortableText value={portableText} />}
            </Typography>
          </a>
        )}
        {!addLink && (
          <Typography
            variant="subtitle"
            size="1"
            component="div"
            noPadding
            weight="700"
            className={clsx(styles.title, addSlider && styles.titleSlider, getThemeCSSClass(theme))}
            color={getTypographyDefaultColor(theme)}>
            {portableText && <CustomPortableText value={portableText} />}
          </Typography>
        )}
        {description && (
          <Typography
            variant="body"
            size="1"
            component="div"
            noPadding
            className={clsx(
              styles.description,
              addSlider && styles.small,
              getThemeCSSClass(theme)
            )}>
            <CustomPortableText value={description} />
          </Typography>
        )}
      </div>
      {addSlider && <div className={styles.sliderBottom} />}
    </div>
  );
};

export default ValuePropCardsV2Item;
